import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./store/authContext";
import { useAuth } from "./store/authContext";

// Pages
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import Campaigns from "./pages/Campaigns";
import Contacts from "./pages/Contacts";
import Analytics from "./pages/Analytics";
import Settings from "./pages/Settings";
import AIPage from "./pages/ai";
import AISettings from "./pages/settings/ai";
import AIWorkflows from "./pages/ai/workflows";
import AIOptimizer from "./pages/ai/optimizer";
import AIBusiness from "./pages/ai/business";
import LegalHub from "./pages/legal";
import LegalTerms from "./pages/legal/terms";
import LegalPrivacy from "./pages/legal/privacy";
import LegalCookies from "./pages/legal/cookies";
import LegalDPA from "./pages/legal/dpa";
import LegalAntiSpam from "./pages/legal/anti-spam";
import LegalAUP from "./pages/legal/aup";
import LegalAIPolicy from "./pages/legal/ai-policy";
import LegalRefunds from "./pages/legal/refunds";
import LegalGDPR from "./pages/legal/gdpr";
import LegalSecurity from "./pages/legal/security";
import Footer from "./components/Footer";
import HomePage from "./pages/Home";
import PricingPage from "./pages/Pricing";
import FeaturesPage from "./pages/Features";
import AboutPage from "./pages/About";
import ContactPage from "./pages/Contact";

// Components
import EmailBuilder from "./components/EmailBuilder";

// Lazy loaded components can be added here for better performance

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <Login />} />
      <Route path="/signup" element={isAuthenticated ? <Navigate to="/" /> : <Signup />} />

      <Route path="/" element={
        <PrivateRoute><Dashboard /></PrivateRoute>
      } />
      
      <Route path="/campaigns" element={
        <PrivateRoute><Campaigns /></PrivateRoute>
      } />
      
      <Route path="/campaigns/new" element={
        <PrivateRoute><EmailBuilder /></PrivateRoute>
      } />
      
      <Route path="/campaigns/edit/:id" element={
        <PrivateRoute><EmailBuilder /></PrivateRoute>
      } />
      
      <Route path="/contacts" element={
        <PrivateRoute><Contacts /></PrivateRoute>
      } />
      
      <Route path="/analytics" element={
        <PrivateRoute><Analytics /></PrivateRoute>
      } />
      
      <Route path="/settings" element={
        <PrivateRoute><Settings /></PrivateRoute>
      } />
      
      {/* Add more routes as you build new pages */}
      <Route path="/automation" element={
        <PrivateRoute><div className="p-8"><h1 className="text-2xl font-bold">Automation - Coming Soon</h1></div></PrivateRoute>
      } />
      
      <Route path="/templates" element={
        <PrivateRoute><div className="p-8"><h1 className="text-2xl font-bold">Templates - Coming Soon</h1></div></PrivateRoute>
      } />
      
      <Route path="/billing" element={
        <PrivateRoute><div className="p-8"><h1 className="text-2xl font-bold">Billing - Coming Soon</h1></div></PrivateRoute>
      } />
      
      <Route path="/pages" element={
        <PrivateRoute><div className="p-8"><h1 className="text-2xl font-bold">Landing Pages - Coming Soon</h1></div></PrivateRoute>
      } />
      
      <Route path="/ai" element={
        <PrivateRoute><AIPage /></PrivateRoute>
      } />
      
      <Route path="/settings/ai" element={
        <PrivateRoute><AISettings /></PrivateRoute>
      } />
      
      <Route path="/ai/workflows" element={
        <PrivateRoute><AIWorkflows /></PrivateRoute>
      } />
      
      <Route path="/ai/optimizer" element={
        <PrivateRoute><AIOptimizer /></PrivateRoute>
      } />
      
      <Route path="/ai/business" element={
        <PrivateRoute><AIBusiness /></PrivateRoute>
      } />
      
      {/* Legal Pages (public) */}
      <Route path="/legal" element={<LegalHub />} />
      <Route path="/legal/terms" element={<LegalTerms />} />
      <Route path="/legal/privacy" element={<LegalPrivacy />} />
      <Route path="/legal/cookies" element={<LegalCookies />} />
      <Route path="/legal/dpa" element={<LegalDPA />} />
      <Route path="/legal/anti-spam" element={<LegalAntiSpam />} />
      <Route path="/legal/aup" element={<LegalAUP />} />
      <Route path="/legal/ai-policy" element={<LegalAIPolicy />} />
      <Route path="/legal/refunds" element={<LegalRefunds />} />
      <Route path="/legal/gdpr" element={<LegalGDPR />} />
      <Route path="/legal/security" element={<LegalSecurity />} />
      
      {/* Marketing Pages (public) */}
      <Route path="/home" element={<HomePage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
